'use client';

import React, { useState } from 'react';
import { Copy, Check, Code2, Zap, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';


function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className="p-1.5 hover:bg-background rounded-md transition-colors text-muted-foreground hover:text-foreground border border-transparent hover:border-border"
            title="Copy to clipboard"
        >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
    );
}

export function Developer() {
    const [copied, setCopied] = useState(false);

    const apiUrl = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8080';

    const copyCode = () => {
        navigator.clipboard.writeText(`curl -X POST ${apiUrl}/api/v1/remove-bg \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "file=@/path/to/image.jpg" \\
  -o result.png`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header Section */}
            <div className="bg-gradient-to-br from-background via-background to-card px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                        <Code2 className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium text-primary">For Developers</span>
                    </div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
                        Powerful Background Removal API
                    </h1>
                    <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                        Integrate enterprise-grade background removal into your application. Simple REST API, built for scale.
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                {/* Features Grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-16">
                    <div className="bg-card border border-border rounded-xl p-6">
                        <CheckCircle2 className="h-8 w-8 text-primary mb-4" />
                        <h3 className="font-semibold text-foreground mb-2">RESTful API</h3>
                        <p className="text-sm text-muted-foreground">
                            Simple HTTP endpoints compatible with any language or framework
                        </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-6">
                        <CheckCircle2 className="h-8 w-8 text-primary mb-4" />
                        <h3 className="font-semibold text-foreground mb-2">99.9% Uptime</h3>
                        <p className="text-sm text-muted-foreground">
                            AWS Serverless infrastructure ensures reliability and performance
                        </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-6">
                        <CheckCircle2 className="h-8 w-8 text-primary mb-4" />
                        <h3 className="font-semibold text-foreground mb-2">HD Quality</h3>
                        <p className="text-sm text-muted-foreground">
                            Support for images up to 25MP with lossless quality
                        </p>
                    </div>
                </div>

                {/* Code Section */}
                <div className="grid lg:grid-cols-2 gap-8 mb-16">
                    {/* Pricing */}
                    <div className="bg-card border border-border rounded-xl p-8">
                        <h2 className="text-2xl font-bold text-foreground mb-6">API Pricing</h2>
                        <div className="mb-8">
                            <p className="text-muted-foreground text-sm mb-2">Cost per request</p>
                            <div className="flex items-baseline gap-2">
                                <span className="text-4xl font-bold text-foreground">1 Credit</span>
                                <span className="text-muted-foreground">per image</span>
                            </div>
                        </div>

                        <div className="space-y-4 mb-8">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium text-foreground">No Setup Fees</p>
                                    <p className="text-sm text-muted-foreground">Start using immediately</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium text-foreground">Plan Based</p>
                                    <p className="text-sm text-muted-foreground">Uses your subscription credits</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium text-foreground">Volume Discounts</p>
                                    <p className="text-sm text-muted-foreground">Available on higher tier plans</p>
                                </div>
                            </div>
                        </div>

                        <Link
                            to="/signup?plan=api"
                            className="inline-flex items-center justify-center w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium gap-2"
                        >
                            Get API Key <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>

                    {/* Code Example */}
                    <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-border bg-background">
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                                <div className="w-3 h-3 rounded-full bg-green-500" />
                            </div>
                            <span className="text-xs text-muted-foreground font-mono">curl example</span>
                            <button
                                onClick={copyCode}
                                className="p-2 hover:bg-border rounded transition-colors text-muted-foreground hover:text-foreground"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </button>
                        </div>
                        <pre className="flex-1 p-6 overflow-x-auto font-mono text-sm text-foreground bg-background">
                            {`curl -X POST ${apiUrl}/api/v1/remove-bg \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "file=@/path/to/image.jpg" \\
  -o result.png`}
                        </pre>
                    </div>
                </div>

                {/* Platform Integration Examples */}
                <div className="bg-card border border-border rounded-xl p-8 mb-16">
                    <h2 className="text-2xl font-bold text-foreground mb-6">Platform Integration Examples</h2>
                    <p className="text-muted-foreground mb-6">Ready-to-use code snippets for popular platforms and frameworks</p>

                    <div className="space-y-6">
                        {/* Flutter */}
                        <div className="border border-border rounded-lg overflow-hidden">
                            <div className="bg-background px-4 py-3 border-b border-border">
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                    <Code2 className="h-4 w-4 text-primary" />
                                    Flutter (Dart)
                                </h3>
                            </div>
                            <pre className="p-4 overflow-x-auto font-mono text-sm text-foreground bg-background">
                                {`import 'package:http/http.dart' as http;
import 'dart:io';

Future<File> removeBackground(File imageFile) async {
  final request = http.MultipartRequest(
    'POST',
      'https://api.bgremover.ai/v1/remove-bg',
  );
  
  request.headers['X-API-Key'] = 'YOUR_API_KEY';
  request.files.add(
    await http.MultipartFile.fromPath('file', imageFile.path),
  );
  
  final response = await request.send();
  
  if (response.statusCode == 200) {
    final bytes = await response.stream.toBytes();
    final file = File('path/to/result.png');
    await file.writeAsBytes(bytes);
    return file;
  } else {
    throw Exception('Failed to remove background');
  }
}`}
                            </pre>
                        </div>

                        {/* Swift iOS */}
                        <div className="border border-border rounded-lg overflow-hidden">
                            <div className="bg-background px-4 py-3 border-b border-border">
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                    <Code2 className="h-4 w-4 text-primary" />
                                    Swift (iOS)
                                </h3>
                            </div>
                            <pre className="p-4 overflow-x-auto font-mono text-sm text-foreground bg-background">
                                {`import UIKit

func removeBackground(image: UIImage, completion: @escaping (UIImage?) -> Void) {
    guard let imageData = image.jpegData(compressionQuality: 0.8) else {
        completion(nil)
        return
    }
    
    let url = URL(string: "https://api.bgremover.ai/v1/remove-bg")!
    var request = URLRequest(url: url)
    request.httpMethod = "POST"
    request.setValue("YOUR_API_KEY", forHTTPHeaderField: "X-API-Key")
    
    let boundary = UUID().uuidString
    request.setValue("multipart/form-data; boundary=\\(boundary)", 
                     forHTTPHeaderField: "Content-Type")
    
    var body = Data()
    body.append("--\\(boundary)\\r\\n".data(using: .utf8)!)
    body.append("Content-Disposition: form-data; name=\\"file\\"; filename=\\"image.jpg\\"\\r\\n".data(using: .utf8)!)
    body.append("Content-Type: image/jpeg\\r\\n\\r\\n".data(using: .utf8)!)
    body.append(imageData)
    body.append("\\r\\n--\\(boundary)--\\r\\n".data(using: .utf8)!)
    
    request.httpBody = body
    
    URLSession.shared.dataTask(with: request) { data, response, error in
        guard let data = data, let resultImage = UIImage(data: data) else {
            completion(nil)
            return
        }
        completion(resultImage)
    }.resume()
}`}
                            </pre>
                        </div>

                        {/* React Native */}
                        <div className="border border-border rounded-lg overflow-hidden">
                            <div className="bg-background px-4 py-3 border-b border-border">
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                    <Code2 className="h-4 w-4 text-primary" />
                                    React Native
                                </h3>
                            </div>
                            <pre className="p-4 overflow-x-auto font-mono text-sm text-foreground bg-background">
                                {`import axios from 'axios';
import { launchImageLibrary } from 'react-native-image-picker';

const removeBackground = async (imageUri) => {
  const formData = new FormData();
  formData.append('file', {
    uri: imageUri,
    type: 'image/jpeg',
    name: 'photo.jpg',
  });

  try {
    const response = await axios.post(
      'https://api.bgremover.ai/v1/remove-bg',
      formData,
      {
        headers: {
          'X-API-Key': 'YOUR_API_KEY',
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error removing background:', error);
    throw error;
  }
};`}
                            </pre>
                        </div>

                        {/* React.js */}
                        <div className="border border-border rounded-lg overflow-hidden">
                            <div className="bg-background px-4 py-3 border-b border-border">
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                    <Code2 className="h-4 w-4 text-primary" />
                                    React.js (Web)
                                </h3>
                            </div>
                            <pre className="p-4 overflow-x-auto font-mono text-sm text-foreground bg-background">
                                {`import axios from 'axios';

const removeBackground = async (imageFile) => {
  const formData = new FormData();
  formData.append('file', imageFile);

  try {
    const response = await axios.post(
      'https://api.bgremover.ai/v1/remove-bg',
      formData,
      {
        headers: {
          'X-API-Key': 'YOUR_API_KEY',
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      }
    );
    
    // Create a URL for the processed image
    const imageUrl = URL.createObjectURL(response.data);
    return imageUrl;
  } catch (error) {
    console.error('Error removing background:', error);
    throw error;
  }
};

// Usage in a React component
function ImageProcessor() {
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const resultUrl = await removeBackground(file);
      // Use resultUrl to display the processed image
    }
  };

  return <input type="file" onChange={handleFileChange} accept="image/*" />;
}`}
                            </pre>
                        </div>
                    </div>
                </div>

                {/* Documentation Section */}
                <div className="bg-card border border-border rounded-xl p-8 mb-16">
                    <h2 className="text-2xl font-bold text-foreground mb-6">Quick Start Guide</h2>
                    <div className="space-y-6">
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-semibold text-sm">1</div>
                                <h3 className="font-semibold text-foreground">Get Your API Key</h3>
                            </div>
                            <p className="text-muted-foreground text-sm ml-11">
                                Sign up for an account and generate an API key from your dashboard
                            </p>
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-semibold text-sm">2</div>
                                <h3 className="font-semibold text-foreground">Make a Request</h3>
                            </div>
                            <p className="text-muted-foreground text-sm ml-11">
                                Send an image file to our API endpoint with your API key in the headers
                            </p>
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-semibold text-sm">3</div>
                                <h3 className="font-semibold text-foreground">Get Results</h3>
                            </div>
                            <p className="text-muted-foreground text-sm ml-11">
                                Receive your processed image with the background removed instantly
                            </p>
                        </div>
                    </div>
                </div>

                {/* API Endpoints */}
                <div className="space-y-8">
                    <h2 className="text-3xl font-bold text-foreground text-center mb-12">API Endpoints</h2>

                    {/* remove-bg */}
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="p-6 border-b border-border">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary text-primary-foreground">POST</span>
                                <code className="text-lg font-mono text-foreground">/api/v1/remove-bg</code>
                            </div>
                            <p className="text-muted-foreground">Remove background from an image. Supports multipart/form-data.</p>
                        </div>
                        <div className="bg-muted/50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Example Request</span>
                                <CopyButton text={`curl -X POST ${apiUrl}/api/v1/remove-bg \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "file=@/path/to/image.jpg" \\
  -o result.png`} />
                            </div>
                            <pre className="overflow-x-auto font-mono text-sm text-foreground bg-background p-4 rounded-lg border border-border">
                                {`curl -X POST ${apiUrl}/api/v1/remove-bg \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -F "file=@/path/to/image.jpg" \\
  -o result.png`}
                            </pre>
                        </div>
                    </div>

                    {/* usage */}
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="p-6 border-b border-border">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-500">GET</span>
                                <code className="text-lg font-mono text-foreground">https://get-usage-stats-vyy5awhcxa-uc.a.run.app</code>
                            </div>
                            <p className="text-muted-foreground">Get your current API usage statistics and history.</p>
                        </div>
                        <div className="bg-muted/50 p-6 space-y-6">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Example: JSON Response</span>
                                    <CopyButton text={`curl -X GET "https://get-usage-stats-vyy5awhcxa-uc.a.run.app?limit=10" \\
  -H "X-API-Key: YOUR_API_KEY"`} />
                                </div>
                                <pre className="overflow-x-auto font-mono text-sm text-foreground bg-background p-4 rounded-lg border border-border">
                                    {`curl -X GET "https://get-usage-stats-vyy5awhcxa-uc.a.run.app?limit=10" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                                </pre>
                            </div>

                            <div className="pt-6 border-t border-border grid md:grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="space-y-1">
                                            <span className="text-xs font-medium text-primary uppercase tracking-wider">CMD Table View</span>
                                            <p className="text-xs text-muted-foreground">View directly in terminal</p>
                                        </div>
                                        <CopyButton text={`curl -X GET "https://get-usage-stats-vyy5awhcxa-uc.a.run.app?limit=10&format=text" \\
  -H "X-API-Key: YOUR_API_KEY"`} />
                                    </div>
                                    <pre className="overflow-x-auto font-mono text-xs text-foreground bg-background p-4 rounded-lg border border-border">
                                        {`curl -X GET "https://get-usage-stats-vyy5awhcxa-uc.a.run.app?limit=10&format=text" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                                    </pre>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="space-y-1">
                                            <span className="text-xs font-medium text-green-500 uppercase tracking-wider">HTML Download View</span>
                                            <p className="text-xs text-muted-foreground">Save as .html for browser</p>
                                        </div>
                                        <CopyButton text={`curl -X GET "https://get-usage-stats-vyy5awhcxa-uc.a.run.app?limit=10&format=table" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -o usage_stats.html`} />
                                    </div>
                                    <pre className="overflow-x-auto font-mono text-xs text-foreground bg-background p-4 rounded-lg border border-border italic">
                                        {`curl -X GET "https://get-usage-stats-vyy5awhcxa-uc.a.run.app?limit=10&format=table" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -o usage_stats.html`}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* key-info */}
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="p-6 border-b border-border">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-500">GET</span>
                                <code className="text-lg font-mono text-foreground">https://get-key-info-vyy5awhcxa-uc.a.run.app</code>
                            </div>
                            <p className="text-muted-foreground">Get your API key details, current credits, and subscription status.</p>
                        </div>
                        <div className="bg-muted/50 p-6 space-y-6">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Example: JSON Response</span>
                                    <CopyButton text={`curl -X GET "https://get-key-info-vyy5awhcxa-uc.a.run.app" \\
  -H "X-API-Key: YOUR_API_KEY"`} />
                                </div>
                                <pre className="overflow-x-auto font-mono text-sm text-foreground bg-background p-4 rounded-lg border border-border">
                                    {`curl -X GET "https://get-key-info-vyy5awhcxa-uc.a.run.app" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                                </pre>
                            </div>

                            <div className="pt-6 border-t border-border grid md:grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="space-y-1">
                                            <span className="text-xs font-medium text-primary uppercase tracking-wider">CMD Table View</span>
                                            <p className="text-xs text-muted-foreground">View directly in terminal</p>
                                        </div>
                                        <CopyButton text={`curl -X GET "https://get-key-info-vyy5awhcxa-uc.a.run.app?format=text" \\
  -H "X-API-Key: YOUR_API_KEY"`} />
                                    </div>
                                    <pre className="overflow-x-auto font-mono text-xs text-foreground bg-background p-4 rounded-lg border border-border">
                                        {`curl -X GET "https://get-key-info-vyy5awhcxa-uc.a.run.app?format=text" \\
  -H "X-API-Key: YOUR_API_KEY"`}
                                    </pre>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="space-y-1">
                                            <span className="text-xs font-medium text-green-500 uppercase tracking-wider">HTML Download View</span>
                                            <p className="text-xs text-muted-foreground">Save as .html for browser</p>
                                        </div>
                                        <CopyButton text={`curl -X GET "https://get-key-info-vyy5awhcxa-uc.a.run.app?format=table" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -o key_info.html`} />
                                    </div>
                                    <pre className="overflow-x-auto font-mono text-xs text-foreground bg-background p-4 rounded-lg border border-border italic">
                                        {`curl -X GET "https://get-key-info-vyy5awhcxa-uc.a.run.app?format=table" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -o key_info.html`}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-primary/10 to-accent/10 border-y border-primary/20 mt-16 py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                        Ready to integrate?
                    </h2>
                    <p className="text-lg text-muted-foreground mb-8">
                        Start building with our API today. No credit card required.
                    </p>
                    <Link
                        to="/signup?plan=api"
                        className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-semibold gap-2"
                    >
                        <Zap className="h-5 w-5" />
                        Get API Key Now
                    </Link>
                </div>
            </div>
        </div>
    );
}
